import {Buffer} from 'buffer';
import isEqual from 'lodash/isEqual';
import {RgbtColor, parseHtmlColor, unparseHtmlColor} from 'pikaparam';

import {Constants} from '../../../../../constants';
import {colors} from '../../../../../styles/colors';
import {LightingPattern} from '../../../../types';
import {IUnitClient} from '../UnitClient';
import {
  type IUnitLedApi,
  type ReadLightOptions,
  type ReadLightResult,
  type WriteLightOptions,
  isMoodLight,
} from './IUnitLedApi';

export class UnitLedApiV1 implements IUnitLedApi {
  constructor(private client: IUnitClient) {}

  public async readActiveLedArray(): Promise<number[]> {
    const value = await this.client.read<RgbtColor>('activeLedColorApi1');

    return Array.from(new Uint8Array(value.as4Byte));
  }

  public async setPartyMode() {
    const rgbt = RgbtColor.from4Byte(
      Buffer.from([...Constants.PARTY_MODE.PRE_T]),
    );

    await this.client.write<RgbtColor>(`lanternColorApi1`, rgbt);
  }

  public async readLight(options: ReadLightOptions): Promise<ReadLightResult> {
    const {target} = options;

    const color = await this.client.read<RgbtColor>(`${target}ColorApi1`);
    const pattern =
      target === 'lantern'
        ? await this.client.read<LightingPattern>('lanternPattern')
        : undefined;

    const [r, g, b] = color.bytes;

    const isPartyMode = this.isPartyMode(color.as4Byte);

    const hexColorTriplet = unparseHtmlColor({r, g, b});

    return {
      light: {
        color: isPartyMode ? colors.defaultColor : hexColorTriplet,
        pattern: isPartyMode
          ? LightingPattern.PARTY_MODE
          : pattern ?? LightingPattern.STEADY,
      },
    };
  }

  public async writeLight(options: WriteLightOptions): Promise<void> {
    const {target, light} = options;

    // Led API v1 max version is R so it doesn't support mood lights
    if (isMoodLight(light)) return;

    const {color, pattern} = light;

    const data = RgbtColor.fromRgb(parseHtmlColor(color));

    await this.client.write<RgbtColor>(`${target}ColorApi1`, data);

    if (target === 'lantern') {
      await this.client.write<number>(
        'lanternPattern',
        pattern ?? LightingPattern.STEADY,
      );
    }
  }

  private isPartyMode(bytes: ArrayBufferLike) {
    return isEqual(
      Array.from(new Uint8Array(bytes)),
      Constants.PARTY_MODE.PRE_T,
    );
  }
}
